import { createRouter, createWebHistory } from 'vue-router'
import { adminGuard } from './guards/adminGuard'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Redirection de /login vers /auth/login
    {
      path: '/login',
      redirect: '/auth/login',
      meta: { requiresAuth: false }
    },

    // Routes d'authentification
    {
      path: '/auth',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [
        {
          path: 'login',
          name: 'login',
          component: () => import('@/views/auth/Login.vue'),
          meta: { requiresAuth: false }
        },
        {
          path: 'request-reset-password',
          name: 'request-reset-password',
          component: () => import('@/views/auth/RequestResetPassword.vue'),
          meta: { requiresAuth: false }
        },
        {
          path: 'callback',
          name: 'auth-callback',
          component: () => import('@/views/auth/Callback.vue'),
          meta: { requiresAuth: false }
        },
        {
          path: 'reset-password',
          name: 'reset-password',
          component: () => import('@/views/auth/ResetPassword.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'access-denied',
          name: 'access-denied',
          component: () => import('@/views/auth/AccessDenied.vue'),
          meta: { requiresAuth: false }
        }
      ]
    },

    // Routes principales
    {
      path: '/',
      redirect: { name: 'Projets' },
      meta: { requiresAuth: true }
    },
    {
      path: '/projects',
      name: 'Projets',
      component: () => import('@/views/ProjectsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/clients',
      name: 'Clients',
      component: () => import('@/views/CustomersView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin',
      name: 'Administration',
      component: () => import('@/views/admin/AdminView.vue'),
      redirect: { name: 'admin-users' },
      meta: { requiresAuth: true },
      beforeEnter: adminGuard,
      children: [
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/views/admin/views/Users.vue'),
          meta: { requiresAuth: true },
          beforeEnter: adminGuard
        },
        {
          path: 'email-templates',
          name: 'admin-email-templates',
          component: () => import('@/views/admin/views/EmailTemplates.vue'),
          meta: { requiresAuth: true },
          beforeEnter: adminGuard
        }
      ]
    },
    {
      path: '/projects/:id',
      component: () => import('@/views/project/ProjectView.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'project-overview',
          component: () => import('@/views/project/views/Overview.vue'),
          props: true,
          meta: { requiresAuth: true }
        },
        {
          path: 'lots',
          name: 'project-lots',
          component: () => import('@/views/project/views/Lots.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'providers',
          name: 'project-providers',
          component: () => import('@/views/project/views/Providers.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'expert-report',
          name: 'project-expert-report',
          component: () => import('@/views/project/views/ExpertReport.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'site-visits',
          name: 'project-site-visits',
          component: () => import('@/views/project/views/SiteVisits.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'documents',
          name: 'project-documents',
          component: () => import('@/views/project/views/Documents.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'budget',
          name: 'project-budget',
          component: () => import('@/views/project/views/Budget.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'markets',
          name: 'project-markets',
          component: () => import('@/views/project/views/Markets.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'access',
          name: 'project-access',
          component: () => import('@/views/project/views/Access.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'settings',
          name: 'project-settings',
          component: () => import('@/views/project/views/Settings.vue'),
          meta: { requiresAuth: true }
        }
      ]
    },
    {
      path: '/projects/:id/new-visit',
      name: 'new-visit',
      component: () => import('@/views/project/views/visits/NewVisit.vue'),
      props: true,
      meta: { requiresAuth: true, fullScreen: true }
    },
    {
      path: '/projects/:id/visit/:visitId',
      name: 'visit-details',
      component: () => import('@/views/project/views/visits/NewVisit.vue'),
      props: true,
      meta: { requiresAuth: true, fullScreen: true }
    },

    // Route 404 - doit être en dernier
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFound.vue'),
      meta: { requiresAuth: false }
    }
  ]
})

router.beforeEach(async (to) => {
  // Lazy import to avoid router <-> store circular dependency on reload
  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()

  const isAuthArea = to.path === '/auth' || to.path.startsWith('/auth/')

  // Do not wait on auth readiness for /auth/* pages, so login/reset render instantly
  if (!isAuthArea && typeof authStore.ensureReady === 'function') {
    await authStore.ensureReady(3000)
  } else if (!isAuthArea) {
    // fallback bounded wait
    const start = Date.now()
    while (authStore.loading && Date.now() - start < 1500) {
      await new Promise((r) => setTimeout(r, 50))
    }
  }

  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)

  // Not authenticated → go to access-denied page
  if (requiresAuth && !authStore.isAuthenticated) {
    return { name: 'access-denied', query: { reason: 'unauthenticated', redirect: to.fullPath } }
  }

  // If authenticated and mustChangePassword is null, eagerly fetch it once
  if (!isAuthArea && authStore.isAuthenticated && authStore.mustChangePassword === null) {
    try {
      if (typeof authStore.fetchMe === 'function') {
        await authStore.fetchMe()
      }
    } catch (_) {}
  }

  // If authenticated but must change password, force the dedicated page
  // Only allow navigating to the dedicated reset-password flow, callback, or login
  const allowDuringPwEnforcement = new Set(['reset-password', 'auth-callback', 'login'])
  if (authStore.mustChangePassword === true) {
    if (!allowDuringPwEnforcement.has(String(to.name))) {
      return { name: 'reset-password' }
    }
  }

  // Already authenticated and trying to access auth pages → send to app home,
  // EXCEPT for reset-password and access-denied which must remain accessible
  if (authStore.isAuthenticated && isAuthArea && to.name !== 'reset-password' && to.name !== 'access-denied') {
    return { name: 'Projets' }
  }

  return true
})

export default router
